import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Admin-only "simulate free plan" preview switch.
 *
 * Compiles are asynchronous (enqueue → queue → drain), so the choice cannot
 * ride along on the request; it is persisted in
 * `writer_user_preferences.extra.simulate_free_mark` and read server-side by
 * `shouldApplyFreeMark`. The server ignores it for non-admins and it can only
 * ever ADD the credit line — never remove it for a free user.
 */
export function useWriterFreeMarkSimulation(): {
  isAdmin: boolean;
  enabled: boolean;
  loading: boolean;
  saving: boolean;
  setEnabled: (next: boolean) => Promise<void>;
} {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [isAdmin, setIsAdmin] = useState(false);
  const [enabled, setEnabledState] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setIsAdmin(false);
      setEnabledState(false);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    (async () => {
      try {
        const { data: roleData, error: roleError } = await supabase.rpc('has_role', {
          _role: 'admin',
        });
        const admin = !roleError && roleData === true;
        if (cancelled) return;
        setIsAdmin(admin);
        if (!admin) {
          setEnabledState(false);
          return;
        }
        const { data } = await supabase
          .from('writer_user_preferences')
          .select('extra')
          .eq('owner_id', userId)
          .maybeSingle();
        if (cancelled) return;
        const extra = ((data as { extra?: unknown } | null)?.extra ?? {}) as Record<
          string,
          unknown
        >;
        setEnabledState(extra.simulate_free_mark === true);
      } catch {
        if (!cancelled) {
          setIsAdmin(false);
          setEnabledState(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const setEnabled = useCallback(
    async (next: boolean) => {
      if (!userId || !isAdmin) return;
      setSaving(true);
      try {
        const { data } = await supabase
          .from('writer_user_preferences')
          .select('extra')
          .eq('owner_id', userId)
          .maybeSingle();
        const extra = ((data as { extra?: unknown } | null)?.extra ?? {}) as Record<
          string,
          unknown
        >;
        const { error } = await supabase.from('writer_user_preferences').upsert(
          { owner_id: userId, extra: { ...extra, simulate_free_mark: next } },
          { onConflict: 'owner_id' },
        );
        if (error) throw new Error(error.message);
        setEnabledState(next);
      } finally {
        setSaving(false);
      }
    },
    [userId, isAdmin],
  );

  return { isAdmin, enabled, loading, saving, setEnabled };
}

export default useWriterFreeMarkSimulation;
